<template>
  <div class="flex flex-col h-full">
    <!-- Overlay de chargement -->
    <div v-if="isGeneratingPdf" class="pdf-loading-overlay">
      <div class="pdf-loading-content loader-standard">
        <div class="spinner" />
        <div class="loading-text">
          Génération du PDF en cours…
        </div>
      </div>
    </div>

    <!-- Modal de sélection des pages pour les flyers -->
    <div v-if="showPageSelectionModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">
            Sélection des pages
          </h3>
          <button class="modal-close" @click="closePageSelectionModal">
            <i class="fas fa-times" />
          </button>
        </div>
        <div class="modal-body">
          <p class="modal-description">
            Choisissez quelles pages vous souhaitez inclure dans votre PDF :
          </p>
          <div class="page-options">
            <label class="page-option">
              <input
                v-model="selectedPages"
                type="radio"
                value="both"
                class="page-radio"
              >
              <span class="page-option-text">
                <i class="fas fa-file-alt" />
                Recto et verso
              </span>
            </label>
            <label class="page-option">
              <input
                v-model="selectedPages"
                type="radio"
                value="recto"
                class="page-radio"
              >
              <span class="page-option-text">
                <i class="fas fa-file-alt" />
                Recto uniquement
              </span>
            </label>
            <label class="page-option">
              <input
                v-model="selectedPages"
                type="radio"
                value="verso"
                class="page-radio"
              >
              <span class="page-option-text">
                <i class="fas fa-file-alt" />
                Verso uniquement
              </span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closePageSelectionModal">
            Annuler
          </button>
          <button class="btn-primary" @click="confirmPageSelection">
            Générer PDF
          </button>
        </div>
      </div>
    </div>

    <!-- Contrôles externes -->
    <div v-if="!preview" class="bg-white p-4 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <button
            class="btn-control"
            :disabled="nbProducts <= 1"
            @click="decreaseProducts"
          >
            <span class="text-xl">-</span>
          </button>
          <span class="text-lg font-medium">{{ nbProducts }} produit{{ nbProducts > 1 ? "s" : "" }}</span>
          <button
            class="btn-control"
            :disabled="
              supportStore.currentSupport?.type === 'depliant'
                ? nbProducts >= 6
                : supportStore.currentSupport?.currentSide === 'verso'
                  ? (supportStore.currentSupport?.versoProducts?.length || 0) >= 6
                  : (supportStore.currentSupport?.products?.length || 0) >= 6
            "
            @click="increaseProducts"
          >
            <span class="text-xl">+</span>
          </button>
        </div>

        <!-- Boutons de contrôle -->
        <div class="flex items-center gap-2">
          <!-- Bouton aperçu -->
          <button
            v-if="!preview"
            class="preview-toggle"
            :class="{ active: isPreviewMode }"
            @click="supportStore.togglePreview()"
          >
            <span class="icon">👁️</span>
            {{ isPreviewMode ? "Mode édition" : "Aperçu" }}
          </button>

          <!-- Boutons génération PDF et HTML -->
          <div
            v-if="
              supportStore.currentSupport?.type !== 'depliant' ||
                supportStore.currentSupport?.currentPage === 1
            "
            class="flex gap-2"
          >
            <button
              class="support-canvas__pdf-btn"
              :disabled="isSaving"
              @click="handlePdfGeneration"
            >
              <i class="fas fa-file-pdf" />
              Générer PDF
            </button>
            <button
              class="support-canvas__html-btn"
              :disabled="isSaving"
              @click="generateAndDownloadHTML"
            >
              <i class="fas fa-code" />
              Générer HTML
            </button>
          </div>
          <div v-else class="text-sm text-gray-500 italic">
            Retournez à la page 1 pour générer le PDF
          </div>
        </div>
      </div>
    </div>

    <!-- Affiche -->
    <div
      :id="`page-${supportStore.currentSupport?.currentPage || 1}`"
      class="support-canvas relative bg-white shadow-lg"
      :class="{ 'preview-mode': isPreviewMode }"
    >
      <!-- En-tête avec logo -->
      <div
        v-if="
          showHeader && supportStore.currentSupport?.currentSide !== 'verso'
        "
        class="support-header bg-white border-b border-gray-200"
        @dragover.prevent="isDraggingOver = 'header'"
        @dragleave.prevent="isDraggingOver = null"
        @drop.prevent="onHeaderDrop"
      >
        <img :src="headerImage" alt="Horizon Vert" class="h-full">
      </div>

      <!-- Zone de titre -->
      <div
        class="support-title-container p-2"
        :style="{ backgroundColor: brand === 'fendt' ? '#005e1e' : '#000000' }"
      >
        <h2
          class="support-title-text text-center text-xl font-bold"
          style="color: #ffffff"
          contenteditable="true"
          @blur="updateTitle"
          v-text="titleText"
        />
      </div>

      <!-- Zone de contenu -->
      <div
        class="support-content"
        :class="{
          'verso-mode': supportStore.currentSupport?.currentSide === 'verso',
        }"
      >
        <div class="products-container" :class="gridClass">
          <!-- Layout 4 produits -->
          <template v-if="nbProducts === 4">
            <div class="flex-row" style="flex: 4">
              <!-- Premier produit (phare) -->
              <div
                class="product-zone"
                style="flex: 6"
                @dragover.prevent="isDraggingOver = 0"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 0)"
              >
                <ProductBlock
                  :product="getProduct(0)"
                  :position="1"
                  :is-preview="isPreviewMode"
                  :is-main-product="true"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  @remove="removeProduct(0)"
                  @update:product="handleProductUpdate(0, $event)"
                />
              </div>
            </div>
            <!-- Ligne du bas : 3 produits identiques -->
            <div class="flex-row" style="flex: 4">
              <div
                class="product-zone"
                style="flex: 1"
                @dragover.prevent="isDraggingOver = 1"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 1)"
              >
                <ProductBlock
                  :product="getProduct(1)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  :block-id="generateBlockId(1)"
                  @remove="removeProduct(1)"
                  @update:product="handleProductUpdate(1, $event)"
                />
              </div>
              <div
                class="product-zone"
                style="flex: 1; margin: 0 0.5rem"
                @dragover.prevent="isDraggingOver = 2"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 2)"
              >
                <ProductBlock
                  :product="getProduct(2)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  :block-id="generateBlockId(2)"
                  @remove="removeProduct(2)"
                  @update:product="handleProductUpdate(2, $event)"
                />
              </div>
              <div
                class="product-zone"
                style="flex: 1"
                @dragover.prevent="isDraggingOver = 3"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 3)"
              >
                <ProductBlock
                  :product="getProduct(3)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  @remove="removeProduct(3)"
                  @update:product="handleProductUpdate(3, $event)"
                />
              </div>
            </div>
          </template>

          <!-- Layout 5 produits -->
          <template v-else-if="nbProducts === 5">
            <!-- Ligne du haut : 2 produits -->
            <div class="flex-row" style="flex: 1; margin-bottom: 1rem">
              <div
                class="product-zone"
                style="flex: 1; margin-right: 0.5rem"
                @dragover.prevent="isDraggingOver = 0"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 0)"
              >
                <ProductBlock
                  :product="getProduct(0)"
                  variant="columnOne"
                  :is-main-product="true"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  :block-id="generateBlockId(0)"
                  @remove="removeProduct(0)"
                  @update:product="handleProductUpdate(0, $event)"
                />
              </div>
              <div
                class="product-zone"
                style="flex: 1; margin-left: 0.5rem"
                @dragover.prevent="isDraggingOver = 1"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 1)"
              >
                <ProductBlock
                  :product="getProduct(1)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  :block-id="generateBlockId(1)"
                  @remove="removeProduct(1)"
                  @update:product="handleProductUpdate(1, $event)"
                />
              </div>
            </div>
            <!-- Ligne du bas : 3 produits -->
            <div class="flex-row" style="flex: 1">
              <div
                class="product-zone"
                style="flex: 1; margin-right: 0.5rem"
                @dragover.prevent="isDraggingOver = 2"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 2)"
              >
                <ProductBlock
                  :product="getProduct(2)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  :block-id="generateBlockId(2)"
                  @remove="removeProduct(2)"
                  @update:product="handleProductUpdate(2, $event)"
                />
              </div>
              <div
                class="product-zone"
                style="flex: 1; margin: 0 0.5rem"
                @dragover.prevent="isDraggingOver = 3"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 3)"
              >
                <ProductBlock
                  :product="getProduct(3)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  @remove="removeProduct(3)"
                  @update:product="handleProductUpdate(3, $event)"
                />
              </div>
              <div
                class="product-zone"
                style="flex: 1; margin-left: 0.5rem"
                @dragover.prevent="isDraggingOver = 4"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 4)"
              >
                <ProductBlock
                  :product="getProduct(4)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  @remove="removeProduct(4)"
                  @update:product="handleProductUpdate(4, $event)"
                />
              </div>
            </div>
          </template>

          <!-- Layout 6 produits -->
          <template v-else-if="nbProducts === 6">
            <!-- Ligne du haut : 3 produits -->
            <div class="flex-row" style="flex: 1; margin-bottom: 0.5rem">
              <div
                class="product-zone"
                style="flex: 1; margin-right: 0.5rem"
                @dragover.prevent="isDraggingOver = 0"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 0)"
              >
                <ProductBlock
                  :product="getProduct(0)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  :block-id="generateBlockId(0)"
                  @remove="removeProduct(0)"
                  @update:product="handleProductUpdate(0, $event)"
                />
              </div>
              <div
                class="product-zone"
                style="flex: 1; margin: 0 0.5rem"
                @dragover.prevent="isDraggingOver = 1"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 1)"
              >
                <ProductBlock
                  :product="getProduct(1)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  :block-id="generateBlockId(1)"
                  @remove="removeProduct(1)"
                  @update:product="handleProductUpdate(1, $event)"
                />
              </div>
              <div
                class="product-zone"
                style="flex: 1; margin-left: 0.5rem"
                @dragover.prevent="isDraggingOver = 2"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 2)"
              >
                <ProductBlock
                  :product="getProduct(2)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  :block-id="generateBlockId(2)"
                  @remove="removeProduct(2)"
                  @update:product="handleProductUpdate(2, $event)"
                />
              </div>
            </div>
            <!-- Ligne du bas : 3 produits -->
            <div class="flex-row" style="flex: 1">
              <div
                class="product-zone"
                style="flex: 1; margin-right: 0.5rem"
                @dragover.prevent="isDraggingOver = 3"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 3)"
              >
                <ProductBlock
                  :product="getProduct(3)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  @remove="removeProduct(3)"
                  @update:product="handleProductUpdate(3, $event)"
                />
              </div>
              <div
                class="product-zone"
                style="flex: 1; margin: 0 0.5rem"
                @dragover.prevent="isDraggingOver = 4"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 4)"
              >
                <ProductBlock
                  :product="getProduct(4)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  @remove="removeProduct(4)"
                  @update:product="handleProductUpdate(4, $event)"
                />
              </div>
              <div
                class="product-zone"
                style="flex: 1; margin-left: 0.5rem"
                @dragover.prevent="isDraggingOver = 5"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 5)"
              >
                <ProductBlock
                  :product="getProduct(5)"
                  variant="columnOne"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  @remove="removeProduct(5)"
                  @update:product="handleProductUpdate(5, $event)"
                />
              </div>
            </div>
          </template>

          <!-- Layout 3 produits -->
          <template v-else-if="nbProducts === 3">
            <div class="flex-column" style="height: 100%">
              <!-- Premier produit en haut -->
              <div
                class="product-zone"
                style="flex: 1; margin-bottom: 1rem"
                @dragover.prevent="isDraggingOver = 0"
                @dragleave.prevent="isDraggingOver = null"
                @drop.prevent="onDrop($event, 0)"
              >
                <ProductBlock
                  :product="getProduct(0)"
                  variant="rowOne"
                  :is-main-product="true"
                  :is-preview="isPreviewMode"
                  :font-family="selectedFont"
                  :font-size="selectedSize"
                  :block-id="generateBlockId(0)"
                  @remove="removeProduct(0)"
                  @update:product="handleProductUpdate(0, $event)"
                />
              </div>
              <!-- Deuxième ligne : deux produits côte à côte -->
              <div class="flex-row" style="flex: 1">
                <div
                  class="product-zone"
                  style="flex: 1; margin-right: 0.5rem"
                  @dragover.prevent="isDraggingOver = 1"
                  @dragleave.prevent="isDraggingOver = null"
                  @drop.prevent="onDrop($event, 1)"
                >
                  <ProductBlock
                    :product="getProduct(1)"
                    variant="columnOne"
                    :position="2"
                    :is-preview="isPreviewMode"
                    :font-family="selectedFont"
                    :font-size="selectedSize"
                    :block-id="generateBlockId(1)"
                    @remove="removeProduct(1)"
                    @update:product="handleProductUpdate(1, $event)"
                  />
                </div>
                <div
                  class="product-zone"
                  style="flex: 1; margin-left: 0.5rem"
                  @dragover.prevent="isDraggingOver = 2"
                  @dragleave.prevent="isDraggingOver = null"
                  @drop.prevent="onDrop($event, 2)"
                >
                  <ProductBlock
                    :product="getProduct(2)"
                    variant="columnOne"
                    :position="3"
                    :is-preview="isPreviewMode"
                    :font-family="selectedFont"
                    :font-size="selectedSize"
                    :block-id="generateBlockId(2)"
                    @remove="removeProduct(2)"
                    @update:product="handleProductUpdate(2, $event)"
                  />
                </div>
              </div>
            </div>
          </template>

          <!-- Layout par défaut pour 1-3 produits -->
          <template v-else>
            <div class="flex-column" style="height: 100%">
              <!-- Layout 1 produit -->
              <template v-if="nbProducts === 1">
                <div
                  class="product-zone"
                  style="flex: 1"
                  :class="{ 'drop-active': isDraggingOver === 0 }"
                  @dragover.prevent="isDraggingOver = 0"
                  @dragleave.prevent="isDraggingOver = null"
                  @drop.prevent="onDrop($event, 0)"
                >
                  <ProductBlock
                    :product="getProduct(0)"
                    variant="columnOne"
                    :is-main-product="true"
                    :is-preview="isPreviewMode"
                    :font-family="selectedFont"
                    :font-size="selectedSize"
                    :block-id="generateBlockId(0)"
                    @remove="removeProduct(0)"
                    @update:product="handleProductUpdate(0, $event)"
                  />
                </div>
              </template>

              <!-- Layout 2 produits -->
              <template v-else-if="nbProducts === 2">
                <div
                  class="product-zone"
                  style="flex: 4"
                  :class="{ 'drop-active': isDraggingOver === 0 }"
                  @dragover.prevent="isDraggingOver = 0"
                  @dragleave.prevent="isDraggingOver = null"
                  @drop.prevent="onDrop($event, 0)"
                >
                  <ProductBlock
                    :product="getProduct(0)"
                    variant="rowOne"
                    :is-main-product="true"
                    :is-preview="isPreviewMode"
                    :font-family="selectedFont"
                    :font-size="selectedSize"
                    :block-id="generateBlockId(0)"
                    @remove="removeProduct(0)"
                    @update:product="handleProductUpdate(0, $event)"
                  />
                </div>
                <div
                  class="product-zone"
                  style="flex: 4"
                  :class="{ 'drop-active': isDraggingOver === 1 }"
                  @dragover.prevent="isDraggingOver = 1"
                  @dragleave.prevent="isDraggingOver = null"
                  @drop.prevent="onDrop($event, 1)"
                >
                  <ProductBlock
                    :product="getProduct(1)"
                    :position="2"
                    variant="rowTwo"
                    :is-preview="isPreviewMode"
                    :font-family="selectedFont"
                    :font-size="selectedSize"
                    :block-id="generateBlockId(1)"
                    @remove="removeProduct(1)"
                    @update:product="handleProductUpdate(1, $event)"
                  />
                </div>
              </template>

              <!-- Layout 3 produits -->
              <template v-else-if="nbProducts === 3">
                <div class="flex-column" style="height: 100%">
                  <!-- Premier produit en haut -->
                  <div
                    class="product-zone"
                    style="flex: 1; margin-bottom: 1rem"
                    @dragover.prevent="isDraggingOver = 0"
                    @dragleave.prevent="isDraggingOver = null"
                    @drop.prevent="onDrop($event, 0)"
                  >
                    <ProductBlock
                      :product="getProduct(0)"
                      variant="rowOne"
                      :is-main-product="true"
                      :is-preview="isPreviewMode"
                      :font-family="selectedFont"
                      :font-size="selectedSize"
                      :block-id="generateBlockId(0)"
                      @remove="removeProduct(0)"
                      @update:product="handleProductUpdate(0, $event)"
                    />
                  </div>
                  <!-- Deuxième ligne : deux produits côte à côte -->
                  <div class="flex-row" style="flex: 1">
                    <div
                      class="product-zone"
                      style="flex: 1; margin-right: 0.5rem"
                      @dragover.prevent="isDraggingOver = 1"
                      @dragleave.prevent="isDraggingOver = null"
                      @drop.prevent="onDrop($event, 1)"
                    >
                      <ProductBlock
                        :product="getProduct(1)"
                        variant="columnOne"
                        :position="2"
                        :is-preview="isPreviewMode"
                        :font-family="selectedFont"
                        :font-size="selectedSize"
                        :block-id="generateBlockId(1)"
                        @remove="removeProduct(1)"
                        @update:product="handleProductUpdate(1, $event)"
                      />
                    </div>
                    <div
                      class="product-zone"
                      style="flex: 1; margin-left: 0.5rem"
                      @dragover.prevent="isDraggingOver = 2"
                      @dragleave.prevent="isDraggingOver = null"
                      @drop.prevent="onDrop($event, 2)"
                    >
                      <ProductBlock
                        :product="getProduct(2)"
                        variant="columnOne"
                        :position="3"
                        :is-preview="isPreviewMode"
                        :font-family="selectedFont"
                        :font-size="selectedSize"
                        :block-id="generateBlockId(2)"
                        @remove="removeProduct(2)"
                        @update:product="handleProductUpdate(2, $event)"
                      />
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>
      </div>

      <!-- Pied de page -->
      <div
        class="support-footer bg-white border-t border-gray-200"
        :class="'h-[115px]'"
      >
        <template
          v-if="
            isFlyer &&
              (supportStore.currentSupport?.type !== 'depliant' ||
                supportStore.currentSupport?.currentPage === 1)
          "
        >
          <div class="flex flex-row h-full items-stretch p-4 gap-4">
            <!-- Colonne gauche : Offres valables, logo, mentions -->
            <div
              class="flex flex-col justify-center flex-1 h-full"
              :class="{
                'w-full':
                  supportStore.currentSupport?.currentSide === 'verso' &&
                  supportStore.currentSupport?.type !== 'depliant',
              }"
            >
              <div class="flex flex-col justify-center h-full">
                <div class="flex items-center mb-1">
                  <div
                    v-if="showValiditySection"
                    class="text-l font-bold leading-tight"
                  >
                    Offres valables
                  </div>
                  <div
                    v-if="showValiditySection"
                    class="text-base text-brand font-semibold leading-tight ml-1"
                    contenteditable="true"
                    @blur="updateValidityPeriod"
                    v-text="validityPeriod"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <img src="/images/logo-agco.svg" alt="AGCO" class="h-8">
                  <span
                    class="text-xs text-gray-600 leading-tight legal-text"
                    :class="{
                      'legal-text-small':
                        hasRepiquage &&
                        (supportStore.currentSupport?.currentSide !== 'verso' ||
                          supportStore.currentSupport?.type === 'depliant'),
                    }"
                  >
                    Validité des textes sous réserve d'erreurs d'impression. Les
                    articles sont assujettis à une TVA de 20%. Port et montage
                    non inclus. {{ brandName }} est une marque AGCO SA. Photos
                    non contractuelles. Dans la limite des stocks disponibles.
                  </span>
                </div>
              </div>
              <button
                v-if="!isPreviewMode"
                class="toggle-validity-button ml-2 mt-2 self-start absolute left-[50%] bottom-[75px]"
                :title="
                  showValiditySection
                    ? 'Masquer la période'
                    : 'Afficher la période'
                "
                @click="showValiditySection = !showValiditySection"
              >
                <span class="icon">{{
                  showValiditySection ? "👁️" : "🚫"
                }}</span>
              </button>
            </div>
            <!-- Colonne droite : Repiquage (uniquement sur le recto ou page 1 dépliant) -->
            <div
              v-if="
                !supportStore.currentSupport?.currentSide ||
                  supportStore.currentSupport?.currentSide === 'recto' ||
                  (supportStore.currentSupport?.type === 'depliant' &&
                    supportStore.currentSupport?.currentPage === 1)
              "
              class="flex flex-col justify-center flex-1"
            >
              <template v-if="selectedRepiquage">
                <div
                  class="h-[135px] bg-white rounded-lg p-2 flex items-center justify-center"
                >
                  <div
                    class="relative w-full h-full flex items-center justify-center"
                  >
                    <div class="w-full h-full flex items-center justify-center">
                      <img
                        :src="selectedRepiquage.logo"
                        :alt="selectedRepiquage.titre"
                        class="max-h-[90px] max-w-[280px] w-auto h-auto object-contain transition-transform duration-200"
                        :style="{
                          transform: `scale(${selectedRepiquage.zoom || 1})`,
                          transformOrigin: 'center center',
                        }"
                      >
                    </div>
                    <button
                      v-if="!isPreviewMode"
                      class="absolute top-1 right-1 text-red-600 hover:text-red-700 bg-white rounded-full p-1 shadow-sm z-10"
                      @click="removeRepiquage"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </template>
              <button
                v-else
                class="bg-white rounded-lg border-2 border-dashed border-gray-300 p-5 text-center cursor-pointer hover:border-brand transition-colors"
                @click="showSignaturesModal = true"
              >
                <div class="flex items-center justify-center">
                  <div
                    class="rounded-full border-2 border-brand w-8 h-8 flex items-center justify-center mb-2"
                  >
                    <span class="text-brand text-2xl">+</span>
                  </div>
                </div>
                <span class="text-gray-600">Ajouter un repiquage adresse(s)</span>
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="h-full items-center justify-center p-4">
            <div class="flex items-center gap-2 mb-2">
              <img src="/images/logo-agco.svg" alt="AGCO" class="h-8">
              <span class="text-xs text-gray-600 leading-tight">
                Validité des textes sous réserve d'erreurs d'impression. Les
                articles sont assujettis à une TVA de 20%. Port et montage non
                inclus. {{ brandName }} est une marque AGCO SA. Photos non
                contractuelles. Dans la limite des stocks disponibles.
              </span>
            </div>
            <div
              v-if="showValiditySection"
              class="text-lg flex items-center gap-2 validity-section justify-center"
            >
              <span class="text-xl font-bold">Offres valables</span>
              <span
                class="text-xl text-brand font-bold"
                contenteditable="true"
                @blur="updateValidityPeriod"
                v-text="validityPeriod"
              />
            </div>
            <button
              v-if="!isPreviewMode"
              class="toggle-validity-button ml-2 mt-2"
              :title="
                showValiditySection
                  ? 'Masquer la période'
                  : 'Afficher la période'
              "
              @click="showValiditySection = !showValiditySection"
            >
              <span class="icon">{{ showValiditySection ? "👁️" : "🚫" }}</span>
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- Modale des signatures -->
    <div
      v-if="showSignaturesModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div
        class="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto"
      >
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-semibold">
            Mes signatures
          </h2>
          <button
            class="text-gray-500 hover:text-gray-700"
            @click="showSignaturesModal = false"
          >
            <span class="text-2xl">×</span>
          </button>
        </div>

        <!-- Contrôle de zoom -->
        <div
          v-if="selectedSignatureForZoom"
          class="mb-4 p-4 border rounded-lg bg-gray-50"
        >
          <div class="flex items-center justify-between mb-2">
            <label class="text-sm font-medium text-gray-700">Ajuster la taille</label>
            <span class="text-sm text-gray-500">{{ zoomLevel }}%</span>
          </div>
          <input
            v-model="zoomLevel"
            type="range"
            min="50"
            max="150"
            step="5"
            class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          >
          <div class="mt-4 flex justify-center">
            <img
              :src="selectedSignatureForZoom.logo"
              :alt="selectedSignatureForZoom.titre"
              class="max-h-[150px] object-contain transition-transform duration-200"
              :style="{ transform: `scale(${zoomLevel / 100})` }"
            >
          </div>
          <div class="mt-4 flex justify-end gap-2">
            <button
              class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              @click="cancelZoom"
            >
              Annuler
            </button>
            <button
              class="px-4 py-2 text-sm bg-brand text-white rounded hover:bg-[#8c0014]"
              @click="confirmZoom"
            >
              Appliquer
            </button>
          </div>
        </div>

        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <div
            v-for="signature in userSignatures"
            :key="signature.id"
            class="border rounded-lg p-4 cursor-pointer hover:border-brand transition-colors"
            @click="startZoom(signature)"
          >
            <img
              :src="signature.logo"
              :alt="signature.titre"
              class="w-full h-32 object-contain mb-2"
            >
            <p class="text-sm text-gray-600 truncate">
              {{ signature.titre }}
            </p>
          </div>
        </div>

        <div v-if="userSignatures.length === 0" class="text-center py-8">
          <p class="text-gray-500">
            Aucune signature disponible
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import api from "@/api/api";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import type { Support, SupportPage } from "@/types/support";
import {
  getJsPDFOptions,
  HIGH_QUALITY_SCALE,
  PDF_IMAGE_COMPRESSION
} from "@/utils/pdfQualityConfig";
import {
  computed,
  defineExpose,
  nextTick,
  onMounted,
  ref,
  watch,
} from "@vue/runtime-core";
import domtoimage from "dom-to-image-more";
import { jsPDF } from "jspdf";
import { useRoute } from "vue-router";
import ProductBlock from "./blocks/ProductBlock.vue";
import { useCurrentBrand, useStorage } from "@/utils/storage";

interface Props {
  support: Support;
  showHeader?: boolean;
  preview?: boolean;
  selectedPages?: string; // Nouvelle prop pour la sélection des pages
}

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string | null;
  reference?: string;
  image_name?: string;
  product_image_id?: number;
  pictogram_id?: number;
  price_ht?: string;
  type?: string;
  product_id?: number;
}

interface Banner {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string;
  reference?: string;
  image_name?: string;
  featured: boolean;
}

interface Signature {
  id: number;
  logo: string;
  titre: string;
  info: string;
  contact: string;
  type: "signature" | "address";
}

const props = withDefaults(defineProps<Props>(), {
  showHeader: true,
  preview: false,
  selectedPages: "both", // Valeur par défaut
});

const supportStore = useSupportStore();
const isDraggingOver = ref<number | null>(null);
const selectedFont = ref<string>("Arial");
const selectedSize = ref<string>("md");
const isPreviewMode = computed<boolean>(() => supportStore.isPreview);
const headerImage = ref<string | null>(null);
const titleText = ref(props.support?.name || "Votre titre");
const banners = ref<Banner[]>([]);
const { brand: reactiveBrand } = useCurrentBrand();
const brand = computed(() => reactiveBrand.value || "agco");
const showValiditySection = ref<boolean>(true);
const isSaving = ref<boolean>(false);
const pages = ref<SupportPage[]>([]);
const route = useRoute();
const hasRepiquage = ref<boolean>(false);
const showSignaturesModal = ref(false);
const userSignaturesStorage = useStorage("user_signatures", []);
const userSignatures = ref<Signature[]>([]);
const selectedRepiquage = ref<Signature | null>(null);
const selectedSignatureForZoom = ref<Signature | null>(null);
const zoomLevel = ref(100);
const validityPeriod = ref(props.support?.dates || props.support?.validity_period || "du XX au XX mois ANNÉE");
const isGeneratingPdf = ref(false);
const products = ref<any[]>([]);

// Variables pour la modal de sélection des pages
const showPageSelectionModal = ref(false);
const selectedPages = ref<string>(props.selectedPages || "both");

// Watcher pour synchroniser la prop selectedPages avec la variable locale
watch(
  () => props.selectedPages,
  (newValue) => {
    if (newValue) {
      selectedPages.value = newValue;
    }
  },
  { immediate: true }
);

const isFlyer = computed(() => {
  return (
    route.path.includes("/supports/flyer/") ||
    route.path.includes("/supports/depliant/")
  );
});

const brandName = computed(() => {
  switch (brand.value) {
    case "agco":
      return "AGCO SA";
    case "hv":
      return "Horizon Vert";
    case "valtra":
      return "Valtra Shop";
    case "fendt":
      return "Lisa Fendt";

    default:
      return "Horizon Vert";
  }
});

// Nombre total de slots de produits (pas seulement les produits remplis)
const nbProducts = computed(() => {
  const currentPage = getCurrentPage.value;
  if (!currentPage) return 1;

  // S'assurer que le tableau elements existe
  if (!Array.isArray(currentPage.elements)) {
    currentPage.elements = [null];
    // Mettre à jour le store de manière asynchrone
    nextTick(() => {
      supportStore.updateSupport({
        pages: supportStore.currentSupport.pages,
      });
    });
  }

  return currentPage.elements.length || 1;
});

const getCurrentPage = computed(() => {
  if (!props.support) return null;

  // Pour les dépliants, on utilise currentPage
  if (supportStore.currentSupport?.type === "depliant") {
    const page = supportStore.currentSupport?.pages.find(
      (page: SupportPage) =>
        page.page_number === supportStore.currentSupport.currentPage
    );

    // Si la page n'existe pas ou n'a pas d'éléments, l'initialiser
    if (page && (!Array.isArray(page.elements) || page.elements.length === 0)) {
      page.elements = [null];
      // Mettre à jour le store de manière asynchrone pour éviter les boucles
      nextTick(() => {
        supportStore.updateSupport({
          pages: supportStore.currentSupport.pages,
        });
      });
    }

    return page;
  }

  // Pour les autres types (flyer, etc.), on garde la logique recto/verso
  return supportStore.currentSupport?.pages.find(
    (page: any) =>
      page.page_number ===
      (supportStore.currentSupport?.currentSide === "verso" ? 2 : 1)
  );
});

const getProduct = (index: number) => {
  const currentPage = getCurrentPage.value;
  if (!currentPage) return null;

  // S'assurer que le tableau elements existe
  if (!Array.isArray(currentPage.elements)) {
    currentPage.elements = [];
  }

  // S'assurer que l'index existe
  while (currentPage.elements.length <= index) {
    currentPage.elements.push(null);
  }

  return currentPage.elements[index] || null;
};

const gridClass = computed(() => {
  const count = nbProducts.value;
  if (count <= 1) return "grid-1";
  if (count === 2) return "grid-2";
  if (count === 3) return "grid-3";
  if (count === 4) return "grid-4";
  if (count === 5) return "grid-5";
  if (count === 6) return "grid-6";
  return "grid-6"; // Fallback pour plus de 6 produits
});

const increaseProducts = () => {
  const currentPage = getCurrentPage.value;
  if (!currentPage || currentPage.elements.length >= 6) return;
  currentPage.elements.push(null);
  supportStore.updateSupport({
    pages: supportStore.currentSupport.pages,
  });
};

const decreaseProducts = () => {
  const currentPage = getCurrentPage.value;
  if (!currentPage || currentPage.elements.length <= 1) return;
  currentPage.elements = currentPage.elements.slice(0, -1);
  supportStore.updateSupport({
    pages: supportStore.currentSupport.pages,
  });
};

const removeProduct = (index: number) => {
  const currentPage = getCurrentPage.value;
  if (!currentPage) return;

  const updatedElements = [...currentPage.elements];
  updatedElements[index] = null;

  const updatedPages = supportStore.currentSupport?.pages.map(
    (page: SupportPage) => {
      if (page.page_number === currentPage.page_number) {
        return { ...page, elements: updatedElements };
      }
      return page;
    }
  );

  supportStore.updateSupport({
    pages: updatedPages,
  });

  products.value[index] = null;
  delete blockIds.value[index];
};

const onDrop = (event: DragEvent, index: number) => {
  isDraggingOver.value = null;
  const productData = event.dataTransfer?.getData("application/json");
  if (!productData) return;

  try {
    const product = JSON.parse(productData);
    // Ajout des champs nécessaires
    if (product && !product.type && product.id) {
      product.type = "product";
      product.product_id = product.id;
    }
    const currentPage = getCurrentPage.value;
    if (!currentPage) return;

    // S'assurer que le tableau elements existe et a la bonne taille
    if (!Array.isArray(currentPage.elements)) {
      currentPage.elements = [];
    }

    while (currentPage.elements.length <= index) {
      currentPage.elements.push(null);
    }

    currentPage.elements[index] = product;

    // Forcer la mise à jour du store
    supportStore.updateSupport({
      pages: supportStore.currentSupport.pages,
    });

    // Forcer la réactivité avec nextTick
    nextTick(() => {
      // Vérifier que l'élément a bien été ajouté
      if (currentPage.elements[index] !== product) {
        currentPage.elements[index] = product;
        supportStore.updateSupport({
          pages: supportStore.currentSupport.pages,
        });
      }
    });
  } catch (error) {
    console.error("Invalid product data:", error);
  }
};

const handleProductUpdate = (index: number, product: Product) => {
  // Ajout des champs nécessaires
  if (product && !product.type && product.id) {
    product.type = "product";
    product.product_id = product.id;
  }
  const currentPage = getCurrentPage.value;
  if (!currentPage) return;

  // S'assurer que le tableau elements existe
  if (!Array.isArray(currentPage.elements)) {
    currentPage.elements = [];
  }

  const updatedElements = [...currentPage.elements];

  // S'assurer que l'index existe
  while (updatedElements.length <= index) {
    updatedElements.push(null);
  }

  updatedElements[index] = product;

  const updatedPages = supportStore.currentSupport?.pages.map(
    (page: SupportPage) => {
      if (page.page_number === currentPage.page_number) {
        return { ...page, elements: updatedElements };
      }
      return page;
    }
  );

  supportStore.updateSupport({
    pages: updatedPages,
  });

  // Forcer la réactivité avec nextTick
  nextTick(() => {
    // Vérifier que la mise à jour a bien été appliquée
    const updatedPage = supportStore.currentSupport?.pages.find(
      (page: SupportPage) => page.page_number === currentPage.page_number
    );
    if (updatedPage && updatedPage.elements[index] !== product) {
      updatedPage.elements[index] = product;
      supportStore.updateSupport({
        pages: supportStore.currentSupport.pages,
      });
    }
  });
};

// Helper pour construire l'URL
const getHeaderImageUrl = (image_name?: string) => {
  const apiUrl = import.meta.env.VITE_API_URL; // Utilisation de import.meta.env pour Vite
  return image_name ? `${apiUrl}/upl/headers/${image_name}` : null;
};

// Nouvelle fonction pour construire l'URL des images de signature
const getSignatureImageUrl = (image_name?: string) => {
  const apiUrl = import.meta.env.VITE_API_URL;
  return image_name ? `${apiUrl}/upl/signatures/${image_name}` : null;
};

const loadUserSignatures = async () => {
  let storedSignatures = userSignaturesStorage.value;

  // Fallback API : si le localStorage est vide (ex. accès direct à l'édition
  // sans passer par le tableau de bord), on va chercher les signatures sur le
  // serveur — sinon la modale affiche « Aucune signature disponible ».
  if (!Array.isArray(storedSignatures) || storedSignatures.length === 0) {
    try {
      const { data } = await api.get("/signature");
      const sigs = (data?.signatures || [])
        .filter((s: any) => s.url)
        .map((s: any) => ({
          id: s.id,
          logo: s.url as string,
          titre: s.image_name || "Signature",
          info: "",
          contact: "",
          type: "signature" as const,
        }));
      userSignaturesStorage.value = sigs;
      storedSignatures = sigs;
    } catch (e) {
      console.warn("Aucune signature trouvée", e);
    }
  }

  if (storedSignatures && Array.isArray(storedSignatures)) {
    // Transformer les URLs des images de signature
    userSignatures.value = storedSignatures
      .map((signature: Signature) => {
        const logoUrl = getSignatureImageUrl(signature.logo.split("/").pop());
        return logoUrl ? { ...signature, logo: logoUrl } : null;
      })
      .filter((sig): sig is Signature => sig !== null);
  }
};

const startZoom = (signature: Signature) => {
  selectedSignatureForZoom.value = signature;
  zoomLevel.value = 100;
};

const cancelZoom = () => {
  selectedSignatureForZoom.value = null;
  zoomLevel.value = 100;
};

const confirmZoom = () => {
  if (selectedSignatureForZoom.value) {
    const signature = {
      ...selectedSignatureForZoom.value,
      zoom: zoomLevel.value / 100,
    };
    selectSignature(signature);
    selectedSignatureForZoom.value = null;
    zoomLevel.value = 100;
  }
};

const selectSignature = (signature: Signature) => {
  selectedRepiquage.value = signature;
  hasRepiquage.value = true;

  // Mettre à jour le store avec la signature
  if (supportStore.currentSupport) {
    supportStore.updateSupport({
      repiquage: signature,
    });
  }

  showSignaturesModal.value = false;
};

const removeRepiquage = () => {
  selectedRepiquage.value = null;
  hasRepiquage.value = false;

  // Mettre à jour le store
  if (supportStore.currentSupport) {
    supportStore.updateSupport({
      repiquage: null,
    });
  }
};

onMounted(async () => {
  try {
    // Charger les bannières
    const bannersResponse = await api.get(
      `/support_headers?groups.slug=${brand.value}&isActive=1`
    );
    const bannerData = Array.isArray(bannersResponse.data)
      ? bannersResponse.data
      : bannersResponse.data?.data || [];
    banners.value = Array.isArray(bannerData) ? bannerData : [];

    // Charger la bannière du support si elle existe
    if (props.support?.support_header_id && banners.value.length > 0) {
      // Chercher la bannière correspondant au support_header_id
      const supportBanner = banners.value.find(
        (banner: any) => banner.id === props.support.support_header_id
      );
      if (supportBanner && supportBanner.image_name) {
        headerImage.value = getHeaderImageUrl(supportBanner.image_name);
      } else {
        // Fallback sur la bannière mise en avant si le support_header_id n'est pas trouvé
        const featuredBanner = banners.value.find(
          (banner: any) => banner.featured
        );
        if (featuredBanner && featuredBanner.image_name) {
          headerImage.value = getHeaderImageUrl(featuredBanner.image_name);
        } else if (banners.value.length > 0 && banners.value[0].image_name) {
          headerImage.value = getHeaderImageUrl(banners.value[0].image_name);
        }
      }
    } else {
      // Si pas de support ou pas de support_header_id, utiliser la bannière par défaut
      const featuredBanner = banners.value.find(
        (banner: any) => banner.featured
      );
      if (featuredBanner && featuredBanner.image_name) {
        headerImage.value = getHeaderImageUrl(featuredBanner.image_name);
      } else if (banners.value.length > 0 && banners.value[0].image_name) {
        headerImage.value = getHeaderImageUrl(banners.value[0].image_name);
      }
    }

    // Initialiser les données du support
    if (props.support) {
      // Mettre à jour le titre
      titleText.value = props.support.name;
      // Mettre à jour les pages avec les données existantes
      const newPages = props.support.pages.map((page: SupportPage) => ({
        ...page,
        elements: (page.elements as any[])
          .filter((element: any) => element !== null)
          .map((element: any) => {
            // Construire l'URL de l'image du produit
            const imageUrl = (element as any).product_image?.image_name
              ? `${import.meta.env.VITE_API_URL}/upl/products/${
                  (element as any).product_image.image_name
                }`
              : null;

            // Construire l'URL de l'image de la marque
            const brandImageUrl = (element as any).brand?.image_name
              ? `${import.meta.env.VITE_API_URL}/upl/brands/${
                  (element as any).brand.image_name
                }`
              : null;

            // Sécuriser le mapping selon le type d'élément
            if ((element as any).type === "product") {
              // Charger les element_settings dans le store de personnalisation
              if (
                (element as any).element_settings &&
                (element as any).product_id
              ) {
                const customizationStore =
                  require("@/stores/supports/useProductCustomizationStore").useProductCustomizationStore();
                customizationStore.setCustomization(
                  (element as any).product_id,
                  {
                    ...(element as any).element_settings,
                  }
                );
              }
              const prod = element as any;
              return {
                ...prod,
                id: prod.id,
                name: prod.name,
                description: prod.description ?? "",
                price: prod.price_ht ? parseFloat(prod.price_ht) : 0,
                price_ttc: prod.price_ttc ? parseFloat(prod.price_ttc) : 0,
                price_old: prod.price_old ? parseFloat(prod.price_old) : 0,
                discount_mode: prod.discount_mode ?? null,
                image: imageUrl,
                reference: prod.hook ?? "",
                pictogram_id: prod.pictogram_id ?? null,
                brand: prod.brand
                  ? {
                      ...prod.brand,
                      image: brandImageUrl,
                    }
                  : null,
              };
            } else {
              // Pour les autres types d'éléments, ne pas mapper les champs spécifiques produits
              return {
                ...(element as any),
                id: (element as any).id,
                image: imageUrl,
                brand: (element as any).brand
                  ? {
                      ...(element as any).brand,
                      image: brandImageUrl,
                    }
                  : null,
              };
            }
          }),
      }));

      pages.value = newPages;

      // Mettre à jour le store
      supportStore.currentSupport = {
        ...props.support,
        products: pages.value[0]?.elements || [],
      };

      // Définir le type de support en fonction du template

      const templateName =
        props.support.type?.toLowerCase() ||
        props.support.support_template_id.toString();

      if (templateName.includes("affiche") || templateName.includes("1")) {
        supportStore.setSupportType("affiche");
        supportStore.currentSupport.support_template_id = 1;
      } else if (templateName.includes("flyer") || templateName.includes("2")) {
        supportStore.setSupportType("flyer");
        supportStore.currentSupport.support_template_id = 2;
      } else if (
        templateName.includes("depliant") ||
        templateName.includes("3")
      ) {
        supportStore.setSupportType("depliant");
        supportStore.currentSupport.support_template_id = 3;

        // Forcer l'initialisation de la page 1 pour les dépliants
        if (!supportStore.currentSupport.currentPage) {
          supportStore.currentSupport.currentPage = 1;
        }

        // S'assurer que toutes les pages ont des éléments
        supportStore.currentSupport.pages.forEach((page: SupportPage) => {
          if (!Array.isArray(page.elements) || page.elements.length === 0) {
            page.elements = [null];
          }
        });

        // Mettre à jour le store
        supportStore.updateSupport({
          currentPage: 1,
          pages: supportStore.currentSupport.pages,
          type: "depliant",
        });
      } else if (
        templateName.includes("banderole") ||
        templateName.includes("4")
      ) {
        supportStore.setSupportType("banderole");
        supportStore.currentSupport.support_template_id = 4;
      } else if (
        templateName.includes("emailing") ||
        templateName.includes("5")
      ) {
        supportStore.setSupportType("emailing");
        supportStore.currentSupport.support_template_id = 5;
      } else if (
        templateName.includes("social") ||
        templateName.includes("6")
      ) {
        supportStore.setSupportType("social");
        supportStore.currentSupport.support_template_id = 6;
      }
    }

    // Charger les signatures
    loadUserSignatures();

    // Charger le repiquage existant si présent
    if (supportStore.currentSupport?.repiquage) {
      selectedRepiquage.value = supportStore.currentSupport.repiquage;
      hasRepiquage.value = true;

      // S'assurer que le repiquage est bien restauré dans le store
      supportStore.updateSupport({
        repiquage: supportStore.currentSupport.repiquage,
      });
    } else if ((props.support as any)?.repiquage) {
      // Si le repiquage n'est pas dans le store mais dans les props
      selectedRepiquage.value = (props.support as any).repiquage;
      hasRepiquage.value = true;

      // Mettre à jour le store avec le repiquage des props
      supportStore.updateSupport({
        repiquage: (props.support as any).repiquage,
      });
    } else {
      // Réinitialiser les variables de repiquage
      selectedRepiquage.value = null;
      hasRepiquage.value = false;
    }

    // Initialiser la période de validité
    // Vérifier d'abord si dates ou validity_period existe
    if (props.support.dates || props.support.validity_period) {
      validityPeriod.value = props.support.dates || props.support.validity_period;
    } else {
      // Si aucune période n'est définie, on utilise la valeur par défaut
      validityPeriod.value = "du XX au XX mois ANNÉE";
      if (supportStore.currentSupport) {
        supportStore.currentSupport.validity_period = validityPeriod.value;
      }
    }
  } catch (error) {
    console.error("Erreur lors de l'initialisation:", error);
  }
});

const onHeaderDrop = (event: DragEvent) => {
  isDraggingOver.value = null;
  const bannerData = event.dataTransfer?.getData("text/plain");
  if (!bannerData) {
    return;
  }

  try {
    const parsedData = JSON.parse(bannerData) as { type: string; data: any };
    if (parsedData.type === "banner") {
      // Mettre à jour l'image de la bannière
      if (parsedData.data?.image_name) {
        headerImage.value = getHeaderImageUrl(parsedData.data.image_name);
      }

      // Mettre à jour le support_header_id dans le store si on a un ID
      if (parsedData.data?.id && supportStore.currentSupport) {
        const headerId = typeof parsedData.data.id === 'string'
          ? parseInt(parsedData.data.id, 10)
          : parsedData.data.id;

        supportStore.updateSupport({
          support_header_id: headerId,
        });

        console.log(`Bannière mise à jour: ID ${headerId}`);
      }
    }
  } catch (error) {
    console.error("onHeaderDrop: Invalid JSON data dropped:", error);
  }
};

const updateTitle = (event: FocusEvent) => {
  const target = event.target as HTMLElement;
  titleText.value = target.innerText;
  if (supportStore.currentSupport) {
    supportStore.currentSupport.name = titleText.value;
  }
};

const updateValidityPeriod = (event: Event) => {
  const target = event.target as HTMLDivElement;
  validityPeriod.value = target.innerText;
  if (supportStore.currentSupport) {
    // Utiliser 'dates' au lieu de 'validity_period' pour être cohérent avec le reste du code
    supportStore.currentSupport.dates = validityPeriod.value;
    supportStore.currentSupport.validity_period = validityPeriod.value;
  }
};

watch(
  () => supportStore.currentSupport?.currentPage,
  (newPage) => {
    if (
      supportStore.currentSupport &&
      supportStore.currentSupport.type === "depliant"
    ) {
      const page = supportStore.currentSupport.pages.find(
        (p: SupportPage) => p.page_number === newPage
      );
      if (
        page &&
        (!Array.isArray(page.elements) || page.elements.length === 0)
      ) {
        page.elements = [null];
        supportStore.updateSupport({
          pages: supportStore.currentSupport.pages,
        });
      }

      // Forcer la réactivité en déclenchant un nextTick
      nextTick(() => {
        // S'assurer que les éléments sont bien initialisés
        if (
          page &&
          Array.isArray(page.elements) &&
          page.elements.length === 0
        ) {
          page.elements = [null];
          supportStore.updateSupport({
            pages: supportStore.currentSupport.pages,
          });
        }
      });
    }
  },
  { immediate: true }
);

// Watcher pour mettre à jour l'image du header quand support_header_id change
watch(
  () => supportStore.currentSupport?.support_header_id,
  (newHeaderId) => {
    if (newHeaderId && banners.value.length > 0) {
      const supportBanner = banners.value.find(
        (banner: any) => banner.id === newHeaderId
      );
      if (supportBanner && supportBanner.image_name) {
        headerImage.value = getHeaderImageUrl(supportBanner.image_name);
      }
    }
  }
);

// Watcher pour synchroniser le titre et les dates depuis le support
watch(
  () => [supportStore.currentSupport?.name, supportStore.currentSupport?.dates, props.support?.name, props.support?.dates],
  ([storeName, storeDates, propsName, propsDates]) => {
    // Prioriser les données du store, sinon utiliser les props
    const newName = storeName || propsName;
    const newDates = storeDates || propsDates;

    // Mettre à jour le titre si présent et différent
    if (newName && newName !== titleText.value && newName !== "Votre titre") {
      titleText.value = newName;
    }

    // Mettre à jour les dates si présentes, différentes et non par défaut
    if (newDates &&
        newDates !== validityPeriod.value &&
        !newDates.includes("XX") &&
        newDates.trim() !== "") {
      validityPeriod.value = newDates;
    }
  },
  { immediate: true, deep: true }
);

// Watcher pour synchroniser le repiquage
watch(
  () => supportStore.currentSupport?.repiquage,
  (newRepiquage) => {
    if (newRepiquage) {
      selectedRepiquage.value = newRepiquage;
      hasRepiquage.value = true;
    } else {
      selectedRepiquage.value = null;
      hasRepiquage.value = false;
    }
  },
  { immediate: true }
);

const generateAllPagesImages = async (): Promise<string[]> => {
  const base64Array: string[] = [];
  if (!supportStore.currentSupport || !supportStore.currentSupport.pages)
    return base64Array;

  const originalPreview = isPreviewMode.value;
  const originalPage = supportStore.currentSupport.currentPage;
  const originalSide = supportStore.currentSupport.currentSide;

  // Activer le mode preview si besoin
  if (!originalPreview) supportStore.togglePreview();

  // Pour les flyers, on génère selon la sélection de l'utilisateur
  if (supportStore.currentSupport.type === "flyer") {
    // Utiliser la prop selectedPages si elle est fournie, sinon utiliser la variable locale
    const pagesToGenerate = props.selectedPages || selectedPages.value;

    // Générer le recto si demandé
    if (pagesToGenerate === "both" || pagesToGenerate === "recto") {
      supportStore.currentSupport.currentSide = "recto";
      await new Promise((resolve) => setTimeout(resolve, 500));
      const rectoCanvas = document.querySelector(
        ".support-canvas"
      ) as HTMLElement;
      if (rectoCanvas) {
        // S'assurer que le header est visible pour le recto
        const headerElement = rectoCanvas.querySelector(".support-header");
        if (headerElement) {
          (headerElement as HTMLElement).style.display = "block";
        }

        rectoCanvas.classList.add("pdf-capture-mode");
        const originalBackground = rectoCanvas.style.backgroundColor;
        rectoCanvas.style.backgroundColor = "#ffffff";

        // S'assurer que tous les éléments sont visibles
        const allElements = rectoCanvas.querySelectorAll("*");
        allElements.forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.visibility = "visible";
            el.style.opacity = "1";
          }
        });

        const rectoDataUrl = await domtoimage.toPng(rectoCanvas, {
          bgcolor: "transparent",
          quality: 1,
          cacheBust: true,
          useCORS: true,
          width: rectoCanvas.offsetWidth * HIGH_QUALITY_SCALE,
          height: rectoCanvas.offsetHeight * HIGH_QUALITY_SCALE,
          style: {
            transform: `scale(${HIGH_QUALITY_SCALE})`,
            transformOrigin: "0 0", // Origine en haut à gauche (équivalent à "top left")
            width: `${rectoCanvas.offsetWidth}px`,
            height: `${rectoCanvas.offsetHeight}px`,
            position: "relative",
            left: "-115px",
            top: "0",
          } as any,
          filter: () => true,
        });

        rectoCanvas.classList.remove("pdf-capture-mode");
        rectoCanvas.style.backgroundColor = originalBackground;
        base64Array.push(rectoDataUrl);
      }
    }

    // Générer le verso si demandé
    if (pagesToGenerate === "both" || pagesToGenerate === "verso") {
      supportStore.currentSupport.currentSide = "verso";
      await new Promise((resolve) => setTimeout(resolve, 500));
      const versoCanvas = document.querySelector(
        ".support-canvas"
      ) as HTMLElement;
      if (versoCanvas) {
        // Cacher le header pour le verso
        const headerElement = versoCanvas.querySelector(".support-header");
        if (headerElement) {
          (headerElement as HTMLElement).style.display = "none";
        }

        // Cacher le repiquage pour le verso
        const repiquageElement = versoCanvas.querySelector(
          ".flex-row .flex-col:last-child"
        );
        if (repiquageElement) {
          (repiquageElement as HTMLElement).style.display = "none";
        }

        versoCanvas.classList.add("pdf-capture-mode");
        const versoBackground = versoCanvas.style.backgroundColor;
        versoCanvas.style.backgroundColor = "#ffffff";

        // S'assurer que tous les éléments sont visibles
        const versoElements = versoCanvas.querySelectorAll("*");
        versoElements.forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.visibility = "visible";
            el.style.opacity = "1";
          }
        });

        const versoDataUrl = await domtoimage.toPng(versoCanvas, {
          bgcolor: "transparent",
          quality: 1,
          cacheBust: true,
          useCORS: true,
          width: versoCanvas.offsetWidth * HIGH_QUALITY_SCALE,
          height: versoCanvas.offsetHeight * HIGH_QUALITY_SCALE,
          style: {
            transform: `scale(${HIGH_QUALITY_SCALE})`,
            transformOrigin: "0 0",
            width: `${versoCanvas.offsetWidth}px`,
            height: `${versoCanvas.offsetHeight}px`,
            position: "relative",
            left: "-115px",
            top: "0",
          } as any,
          filter: () => true,
        });

        versoCanvas.classList.remove("pdf-capture-mode");
        versoCanvas.style.backgroundColor = versoBackground;
        base64Array.push(versoDataUrl);
      }
    }
  } else if (supportStore.currentSupport.type === "depliant") {
    // Pour les dépliants, on parcourt toutes les pages
    for (const page of supportStore.currentSupport.pages) {
      supportStore.currentSupport.currentPage = page.page_number;
      await new Promise((resolve) => setTimeout(resolve, 500));
      const canvasElement = document.querySelector(
        ".support-canvas"
      ) as HTMLElement;
      if (!canvasElement) continue;

      // Gérer la visibilité du header selon la page
      const headerElement = canvasElement.querySelector(".support-header");
      if (headerElement) {
        (headerElement as HTMLElement).style.display =
          page.page_number === 1 ? "block" : "none";
      }

      canvasElement.classList.add("pdf-capture-mode");
      const originalBackground = canvasElement.style.backgroundColor;
      canvasElement.style.backgroundColor = "#ffffff";

      // S'assurer que tous les éléments sont visibles
      const allElements = canvasElement.querySelectorAll("*");
      allElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.visibility = "visible";
          el.style.opacity = "1";
        }
      });

      const dataUrl = await domtoimage.toPng(canvasElement, {
        bgcolor: "transparent",
        quality: 1,
        cacheBust: true,
        useCORS: true,
        width: canvasElement.offsetWidth * HIGH_QUALITY_SCALE,
        height: canvasElement.offsetHeight * HIGH_QUALITY_SCALE,
        style: {
          transform: `scale(${HIGH_QUALITY_SCALE})`,
          transformOrigin: "0 0",
          width: `${canvasElement.offsetWidth}px`,
          height: `${canvasElement.offsetHeight}px`,
          position: "relative",
          left: "-115px",
          top: "0",
        } as any,
        filter: () => true,
      });

      canvasElement.classList.remove("pdf-capture-mode");
      canvasElement.style.backgroundColor = originalBackground;
      base64Array.push(dataUrl);
    }
  } else {
    // Pour les autres types de supports (affiches, etc.), on garde la logique par page
    for (const page of supportStore.currentSupport.pages) {
      supportStore.currentSupport.currentPage = page.page_number;
      await new Promise((resolve) => setTimeout(resolve, 500));
      const canvasElement = document.querySelector(
        ".support-canvas"
      ) as HTMLElement;
      if (!canvasElement) continue;

      // Pour les autres types, le header est toujours visible
      const headerElement = canvasElement.querySelector(".support-header");
      if (headerElement) {
        (headerElement as HTMLElement).style.display = "block";
      }

      canvasElement.classList.add("pdf-capture-mode");
      const originalBackground = canvasElement.style.backgroundColor;
      canvasElement.style.backgroundColor = "#ffffff";

      // S'assurer que tous les éléments sont visibles
      const allElements = canvasElement.querySelectorAll("*");
      allElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.visibility = "visible";
          el.style.opacity = "1";
        }
      });

      const dataUrl = await domtoimage.toPng(canvasElement, {
        bgcolor: "transparent",
        quality: 1,
        style: {
          boxShadow: "none",
          margin: 0,
          padding: 0,
          background: "transparent",
        },
        cacheBust: true,
        useCORS: true,
        width: canvasElement.offsetWidth,
        height: canvasElement.offsetHeight,
        filter: () => true,
      });

      canvasElement.classList.remove("pdf-capture-mode");
      canvasElement.style.backgroundColor = originalBackground;
      base64Array.push(dataUrl);
    }
  }

  // Restaurer l'état initial
  supportStore.currentSupport.currentPage = originalPage;
  supportStore.currentSupport.currentSide = originalSide;
  if (!originalPreview) supportStore.togglePreview();
  return base64Array;
};

// Génère le PDF multi-pages à partir du DOM et retourne le base64
const generatePdfBase64 = async (): Promise<string> => {
  const base64Array: string[] = await generateAllPagesImages();
  const pdf = new jsPDF(getJsPDFOptions());
  const pageWidth = 210;
  for (let i = 0; i < base64Array.length; i++) {
    const img = new window.Image();
    img.src = base64Array[i];
    await new Promise((resolve) => {
      img.onload = resolve;
    });
    const imgHeight = (img.height * pageWidth) / img.width;
    if (i > 0) pdf.addPage();
    pdf.addImage(
      base64Array[i],
      "PNG",
      0,
      0,
      pageWidth,
      imgHeight,
      undefined,
      PDF_IMAGE_COMPRESSION
    );
  }
  return pdf.output("datauristring");
};

// Nouvelle méthode : génère le HTML complet avec CSS
const generateFullHTML = async (): Promise<string> => {
  if (!supportStore.currentSupport || !supportStore.currentSupport.pages) {
    return '';
  }

  // Sauvegarder l'état actuel pour le restaurer après
  const originalPreview = isPreviewMode.value;
  const originalPage = supportStore.currentSupport.currentPage;
  const originalSide = supportStore.currentSupport.currentSide;
  const currentValidityPeriod = validityPeriod.value; // Sauvegarder la période de validité
  const currentTitleText = titleText.value; // Sauvegarder le titre

  // Activer le mode preview si besoin
  if (!originalPreview) supportStore.togglePreview();

  // Récupérer tous les styles CSS de la page
  const getAllStyles = () => {
    let styles = '';

    // Récupérer tous les stylesheets
    Array.from(document.styleSheets).forEach(styleSheet => {
      try {
        Array.from(styleSheet.cssRules).forEach(rule => {
          styles += rule.cssText + '\n';
        });
      } catch (e) {
        // Ignorer les erreurs CORS pour les stylesheets externes
        console.warn('Cannot access stylesheet:', e);
      }
    });

    // Ajouter les styles inline
    const styleElements = document.querySelectorAll('style');
    styleElements.forEach(style => {
      styles += style.innerHTML + '\n';
    });

    return styles;
  };

  let htmlContent = '';
  const allStyles = getAllStyles();

  // Pour les flyers, générer selon la sélection
  if (supportStore.currentSupport.type === "flyer") {
    const pagesToGenerate = props.selectedPages || selectedPages.value;

    if (pagesToGenerate === "both" || pagesToGenerate === "recto") {
      supportStore.currentSupport.currentSide = "recto";
      await new Promise((resolve) => setTimeout(resolve, 500));

      const rectoCanvas = document.querySelector(".support-canvas") as HTMLElement;
      if (rectoCanvas) {
        htmlContent += `
          <div class="page recto-page">
            ${rectoCanvas.outerHTML}
          </div>
        `;
      }
    }

    if (pagesToGenerate === "both" || pagesToGenerate === "verso") {
      supportStore.currentSupport.currentSide = "verso";
      await new Promise((resolve) => setTimeout(resolve, 500));

      const versoCanvas = document.querySelector(".support-canvas") as HTMLElement;
      if (versoCanvas) {
        htmlContent += `
          <div class="page verso-page">
            ${versoCanvas.outerHTML}
          </div>
        `;
      }
    }
  } else {
    // Pour les autres types de supports
    for (let pageIndex = 0; pageIndex < supportStore.currentSupport.pages.length; pageIndex++) {
      supportStore.currentSupport.currentPage = pageIndex;
      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = document.querySelector(".support-canvas") as HTMLElement;
      if (canvas) {
        htmlContent += `
          <div class="page page-${pageIndex + 1}">
            ${canvas.outerHTML}
          </div>
        `;
      }
    }
  }

  // Restaurer l'état original et préserver les données importantes
  supportStore.currentSupport.currentPage = originalPage;
  supportStore.currentSupport.currentSide = originalSide;
  if (!originalPreview) supportStore.togglePreview();

  // S'assurer que les données importantes sont préservées
  validityPeriod.value = currentValidityPeriod;
  titleText.value = currentTitleText;
  
  // Forcer la mise à jour dans le store si nécessaire
  if (supportStore.currentSupport) {
    supportStore.currentSupport.validity_period = currentValidityPeriod;
    supportStore.currentSupport.dates = currentValidityPeriod;
  }

  // Remplacer les références aux images locales par des URLs complètes pour tous les logos de marques
  const apiUrl = import.meta.env.VITE_API_URL || 'http://agco.test:8000';
  const htmlContentWithFixedImages = htmlContent
    .replace(/src="\/images\/logo-agco\.svg"/g, `src="${apiUrl}/upl/assets/logo-agco.svg"`)
    .replace(/src='\/images\/logo-agco\.svg'/g, `src="${apiUrl}/upl/assets/logo-agco.svg"`)
    .replace(/src="\/images\/logo-fendt\.svg"/g, `src="${apiUrl}/upl/assets/logo-fendt.svg"`)
    .replace(/src='\/images\/logo-fendt\.svg'/g, `src="${apiUrl}/upl/assets/logo-fendt.svg"`)
    .replace(/src="\/images\/logo-valtra\.svg"/g, `src="${apiUrl}/upl/assets/logo-valtra.svg"`)
    .replace(/src='\/images\/logo-valtra\.svg'/g, `src="${apiUrl}/upl/assets/logo-valtra.svg"`)
    .replace(/src="\/images\/logo-valtra-black\.svg"/g, `src="${apiUrl}/upl/assets/logo-valtra-black.svg"`)
    .replace(/src='\/images\/logo-valtra-black\.svg'/g, `src="${apiUrl}/upl/assets/logo-valtra-black.svg"`)
    .replace(/src="\/images\/logo-hv\.svg"/g, `src="${apiUrl}/upl/assets/logo-hv.svg"`)
    .replace(/src='\/images\/logo-hv\.svg'/g, `src="${apiUrl}/upl/assets/logo-hv.svg"`);

  // Construire le HTML complet
  const fullHTML = `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${titleText.value || 'Support'}</title>
      <style>
        /* Styles de base pour l'impression */
        body {
          margin: 0;
          padding: 20px;
          font-family: Arial, sans-serif;
          background: #f5f5f5;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 100vh;
        }

        .page {
          page-break-after: always;
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto 20px auto;
          background: white;
          position: relative;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          border-radius: 4px;
          overflow: hidden;
        }

        .page:last-child {
          page-break-after: avoid;
          margin-bottom: 0;
        }

        /* Styles pour l'impression */
        @media print {
          body {
            background: white;
            padding: 0;
          }

          .page {
            page-break-after: always;
            margin: 0;
            box-shadow: none;
            border-radius: 0;
          }
        }

        /* Tous les styles CSS existants */
        ${allStyles}
      </style>
    </head>
    <body>
      ${htmlContentWithFixedImages}
    </body>
    </html>
  `;

  return fullHTML;
};

// Nouvelle méthode : génère le PDF et retourne un Blob
const generatePdfBlob = async (): Promise<Blob> => {
  const base64Array: string[] = await generateAllPagesImages();
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  const pageWidth = 210;

  for (let i = 0; i < base64Array.length; i++) {
    const img = new window.Image();
    img.src = base64Array[i];
    await new Promise((resolve) => {
      img.onload = resolve;
    });
    const imgHeight = (img.height * pageWidth) / img.width;
    if (i > 0) pdf.addPage();
    pdf.addImage(
      base64Array[i],
      "PNG",
      0,
      0,
      pageWidth,
      imgHeight,
      undefined,
      "SLOW"
    );
  }
  return pdf.output("blob");
};

// Méthode pour générer et télécharger le PDF côté utilisateur
const generateAndDownloadPDF = async () => {
  try {
    isGeneratingPdf.value = true;
    const pdfBlob = await generatePdfBlob();
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${titleText.value || "support"}-${
      new Date().toISOString().split("T")[0]
    }.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } finally {
    isGeneratingPdf.value = false;
  }
};

// Méthode pour générer et télécharger le HTML côté utilisateur
const generateAndDownloadHTML = async () => {
  try {
    isGeneratingPdf.value = true;
    const htmlContent = await generateFullHTML();
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(htmlBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${titleText.value || "support"}-${
      new Date().toISOString().split("T")[0]
    }.html`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } finally {
    isGeneratingPdf.value = false;
  }
};

defineExpose({
  titleText,
  validityPeriod,
  generatePdfBase64,
  generatePdfBlob,
  generateFullHTML,
  generateAndDownloadHTML,
});

// Ajouter dans la section script setup
const blockIds = ref<{ [key: number]: string }>({});

// Générer un ID de bloc unique
const generateBlockId = (index: number) => {
  if (!blockIds.value[index]) {
    blockIds.value[index] = `block-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  }
  return blockIds.value[index];
};

// Méthode pour gérer la génération du PDF avec modal pour les flyers
const handlePdfGeneration = () => {
  if (supportStore.currentSupport?.type === "flyer") {
    // Afficher la modal de sélection des pages pour les flyers
    showPageSelectionModal.value = true;
  } else {
    // Pour les autres types, générer directement le PDF
    generateAndDownloadPDF();
  }
};

// Fonctions de gestion de la modal
const closePageSelectionModal = () => {
  showPageSelectionModal.value = false;
  selectedPages.value = "both"; // Reset à la valeur par défaut
};

const confirmPageSelection = async () => {
  showPageSelectionModal.value = false;
  await generateAndDownloadPDF();
};

// Ajouter des watchers pour les changements du support
watch(
  () => props.support?.name,
  (newName) => {
    if (newName) {
      titleText.value = newName;
    }
  }
);

watch(
  () => props.support?.dates,
  (newDates) => {
    if (newDates) {
      validityPeriod.value = newDates;
    }
  }
);

// Watcher pour les supports qui auraient validity_period au lieu de dates
watch(
  () => props.support?.validity_period,
  (newValidityPeriod) => {
    if (newValidityPeriod) {
      validityPeriod.value = newValidityPeriod;
    }
  }
);
</script>

<script lang="ts" type="module">
export default {
  name: "SupportCanvas",
};
</script>

<style scoped>
.support-canvas {
  width: 210mm;
  height: 297mm;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.support-content {
  flex: 1;
  padding: 1.5rem;
  padding-bottom: 0.5rem;
  min-height: 0;
  height: calc(297mm - 200px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Mode verso : adaptation des gabarits sans header */
.support-content.verso-mode {
  height: calc(297mm - 115px); /* Hauteur totale moins le footer seulement */
  padding-top: 2rem; /* Plus d'espace en haut car pas de header */
}

/* Adaptation des gabarits ProductBlock en mode verso */
.verso-mode .products-container {
  height: 100%;
}

/* Pour les layouts avec 1-2 produits en mode verso */
.verso-mode .products-container[class*="grid-1"] .product-zone,
.verso-mode .products-container[class*="grid-2"] .product-zone {
  min-height: 280px; /* Plus d'espace disponible */
}

/* Pour les layouts avec 3-6 produits en mode verso */
.verso-mode .products-container[class*="grid-3"] .product-zone,
.verso-mode .products-container[class*="grid-4"] .product-zone,
.verso-mode .products-container[class*="grid-5"] .product-zone,
.verso-mode .products-container[class*="grid-6"] .product-zone {
  min-height: 200px; /* Plus d'espace disponible */
}

/* Adaptation spécifique pour les images en mode verso */
.verso-mode .product-zone :deep(.image-container) {
  max-height: 85%; /* Plus d'espace pour les images */
}

/* Adaptation du contenu produit en mode verso */
.verso-mode .product-zone :deep(.product-info) {
  min-height: 100px; /* Plus d'espace pour le texte */
}

/* Adaptation des titres en mode verso */
.verso-mode .product-zone :deep(.product-title) {
  font-size: 1.6rem; /* Titres légèrement plus grands */
}

/* Adaptation des descriptions en mode verso */
.verso-mode .product-zone :deep(.product-description) {
  font-size: 1.1rem; /* Descriptions légèrement plus grandes */
  -webkit-line-clamp: 5; /* Plus de lignes possibles */
  line-clamp: 5; /* Standard property for compatibility */
}

/* Adaptation des prix en mode verso */
.verso-mode .product-zone :deep(.price-ht) {
  font-size: 1.3rem; /* Prix légèrement plus grands */
}

.verso-mode .products-container[class*="grid-5"] :deep(.product-description) {
  margin-bottom: 4.5em !important;
  font-size: 0.8em;
}

.verso-mode .product-zone :deep(.price-ttc) {
  font-size: 0.8rem; /* Prix TTC légèrement plus grands */
}

.verso-mode .products-container[class*="grid-5"] .is-main .product-title {
  font-size: 1.6rem;
  margin-bottom: 0;
}

.verso-mode .products-container[class*="grid-6"] :deep(.product-description) {
  margin-bottom: 4.5em !important;
  font-size: 0.8em;
}

.products-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.flex-row {
  display: flex;
  gap: 0.5rem;
  min-height: 0;
  flex: 1;
}

.flex-column {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  height: 100%;
}

.product-zone {
  flex: 1;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 2px dashed #ccc;
  border-radius: 0.5rem;
  transition: border-color 0.2s, background 0.2s;
}

:deep(.brand-zone),
:deep(.picto-zone) {
  border: 1px dashed #ccc;
  border-radius: 0.5rem;
  transition: border-color 0.2s, background 0.2s;
}

.preview-mode .product-zone,
.preview-mode :deep(.brand-zone),
.preview-mode :deep(.picto-zone) {
  border: none !important;
  background: transparent !important;
}

/* Ajustement de la taille du texte pour les layouts avec plusieurs produits */
.products-container[class*="grid-4"] :deep(.product-title),
.products-container[class*="grid-5"] :deep(.product-title),
.products-container[class*="grid-6"] :deep(.product-title) {
  font-size: 0.8em; /* Réduction de 20% de la taille du titre */
}

/* Style spécifique pour les 3 produits de droite dans le layout à 5 produits */
.products-container[class*="grid-5"] .flex-column:last-child {
  flex: 3; /* Réduit la largeur de la colonne de droite */
}

.products-container[class*="grid-5"] .flex-column:last-child .product-zone {
  height: 30%; /* Hauteur fixe pour chaque zone de produit */
  min-height: 150px; /* Hauteur minimale */
  margin-bottom: 0.5rem; /* Espacement réduit entre les produits */
}

.products-container[class*="grid-5"]
  .flex-column:last-child
  :deep(.product-image-container) {
  height: 80px; /* Hauteur fixe pour le conteneur d'image */
  width: 80px; /* Largeur fixe pour le conteneur d'image */
  margin: 0 auto; /* Centrage horizontal */
}

.products-container[class*="grid-5"]
  .flex-column:last-child
  :deep(.product-image) {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.products-container[class*="grid-5"]
  .flex-column:last-child
  :deep(.product-content) {
  padding: 0.25rem; /* Réduction du padding */
}

.products-container[class*="grid-5"]
  .flex-column:last-child
  :deep(.product-title),
.products-container[class*="grid-5"]
  .flex-column:last-child
  :deep(.product-description) {
  font-size: 0.75em; /* Réduction de la taille du texte */
  margin: 0.25rem 0; /* Réduction des marges */
}

.products-container[class*="grid-5"]
  .flex-column:last-child
  :deep(.product-price) {
  font-size: 0.85em;
  margin-top: 0.25rem;
}

.verso-mode .products-container[class*="grid-4"] :deep(.product-description) {
  margin-bottom: 4.5rem !important;
  font-size: 0.8em; /* Réduction de 20% de la taille de la description */
}

.products-container[class*="grid-5"] :deep(.product-description),
.products-container[class*="grid-6"] :deep(.product-description) {
  font-size: 0.8em; /* Réduction de 20% de la taille de la description */
  margin-bottom: 1.5rem !important;
}

.products-container[class*="grid-4"] :deep(.product-price),
.products-container[class*="grid-5"] :deep(.product-price),
.products-container[class*="grid-6"] :deep(.product-price) {
  font-size: 0.85em; /* Réduction de 15% de la taille du prix */
}

/* Optimisation de l'espace pour les images */
.products-container[class*="grid-4"] :deep(.product-image),
.products-container[class*="grid-5"] :deep(.product-image),
.products-container[class*="grid-6"] :deep(.product-image) {
  max-height: 100%; /* Augmentation de 60% à 80% pour les images */
  object-fit: contain;
}

/* Ajustement des marges pour le texte */
.products-container[class*="grid-4"] :deep(.product-content),
.products-container[class*="grid-5"] :deep(.product-content),
.products-container[class*="grid-6"] :deep(.product-content) {
  padding: 0.5rem; /* Réduction des marges internes */
  gap: 0.25rem; /* Réduction de l'espace entre les éléments */
}

/* Ajustement spécifique pour les layouts 4-6 produits */
.products-container[class*="grid-4"],
.products-container[class*="grid-5"],
.products-container[class*="grid-6"] {
  height: 100%;
}

/* Pour les layouts avec 5-6 produits */
.flex-column .product-zone {
  height: auto; /* Supprime la hauteur fixe */
  flex: 1; /* Distribution égale de l'espace */
}

.product-zone:hover {
  border-color: #b70018;
}

.drop-active {
  border-color: #b70018;
  background-color: rgba(183, 0, 24, 0.1);
}

.product-zone.has-product {
  border-style: solid;
}

.product-zone.selected {
  background-color: #fff8dc;
}

.btn-control {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-control:hover:not(:disabled) {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.btn-control:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.style-select {
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: white;
  min-width: 120px;
}

.preview-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: white;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.preview-toggle:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.preview-toggle.active {
  background-color: #b70018;
  border-color: #b70018;
  color: white;
}

.preview-toggle .icon {
  font-size: 1.2rem;
}

/* Mode aperçu */

/* Masquer les contrôles d'édition, boutons spécifiques et états vides DANS LES ENFANTS */
.preview-mode :deep(.empty-state),
.preview-mode :deep(.remove-button),             /* Bouton suppression produit */
.preview-mode :deep(.background-toggle),         /* Bouton toggle fond image */
.preview-mode :deep(.background-color-toggle),   /* Bouton couleur fond image */
.preview-mode :deep(.picto-controls),            /* Contrôles taille picto */
.preview-mode :deep(.color-picker),              /* Popup sélécteur couleur */
.preview-mode :deep(.edit-controls),           /* Classe générique potentielle */
.preview-mode :deep(.close-button),            /* Classe générique potentielle */
.preview-mode :deep(.toggle-price-button),     /* Bouton afficher/masquer prix */
.preview-mode :deep(.product-controls) {
  /* Classe générique potentielle */
  display: none !important;
}
.preview-mode button {
  display: none !important;
}
/* Masquer les boutons couleur du titre (qui sont directs) */
.preview-mode .color-picker-toggle {
  display: none !important;
}

/* Supprimer les bordures et fonds des zones interactives (directes et enfants) */
.preview-mode .product-zone, /* Zone directe pour la bordure */
.preview-mode :deep(.picto-zone),              /* Zone picto dans ProductBlock */
.preview-mode :deep(.brand-zone),              /* Zone marque dans ProductBlock */
.preview-mode .support-header {
  /* Header direct */
  border: none !important;
  background: transparent !important; /* Assurer fond transparent */
  padding: 0 !important; /* Peut être nécessaire pour certaines zones */
  cursor: default !important;
}

/* Supprimer les effets de survol en mode aperçu (directs et enfants) */
.preview-mode .product-zone:hover,
.preview-mode :deep(.picto-zone:hover),
.preview-mode :deep(.brand-zone:hover) {
  border: none !important;
  background: transparent !important;
}

/* Supprimer le style lors du drag-over en mode aperçu */
.preview-mode .drop-active {
  border: none !important;
  background: transparent !important;
}

/* Rendre le contenu non éditable visuellement et fonctionnellement DANS LES ENFANTS */
.preview-mode :deep([contenteditable="true"]) {
  outline: none !important;
  cursor: default !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
}

/* Styles généraux pour le canvas en mode aperçu */
.preview-mode .support-canvas {
  box-shadow: none !important;
}

.preview-mode .support-footer {
  border: none !important;
}

/* Styles pour les blocs produits enfants en mode aperçu */
.preview-mode .product-block {
  /* Assure qu'aucune bordure/fond ne subsiste */
  border: none !important;
  background: transparent !important;
}

.preview-mode .product-block * {
  cursor: default !important;
}

/* Masquage redondant potentiel mais explicite pour les éléments internes au ProductBlock */
.preview-mode .product-block .close-button,
.preview-mode .product-block .edit-button,
.preview-mode .product-block .color-picker,
.preview-mode .product-block .controls,
.preview-mode .product-block .price-controls {
  display: none !important;
}

@media print {
  .support-canvas {
    box-shadow: none;
    margin: 0;
    padding: 0;
  }

  .product-zone {
    border: none !important;
    padding: 0;
  }

  .support-header,
  .support-footer {
    position: fixed;
    width: 100%;
    background-color: white;
    z-index: 100;
  }

  .support-header {
    top: 0;
  }

  .support-footer {
    bottom: 0;
  }

  .support-content {
    margin: 80px 0;
  }
}

.support-header {
  border: 2px dashed #ccc;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.support-header.drop-active {
  border-color: #b70018;
  background-color: rgba(183, 0, 24, 0.1);
}

.support-title-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.support-title-text {
  outline: none;
}

.preview-mode .support-title-container input[type="color"] {
  display: none;
}

.preview-mode .support-title-text[contenteditable="true"] {
  outline: none !important;
  cursor: default !important;
  user-select: none !important;
}

.color-picker-toggle {
  background-color: #f9fafb; /* Fond très clair */
  border: 1px solid #d1d5db; /* Bordure grise standard */
  border-radius: 0.375rem; /* Coins arrondis (style Tailwind 'rounded-md') */
  padding: 0.25rem 0.75rem; /* Espacement interne ajusté */
  font-size: 0.875rem; /* Taille de police standard (style Tailwind 'text-sm') */
  font-weight: 500; /* Graisse medium */
  color: #374151; /* Couleur de texte foncée */
  cursor: pointer;
  transition: all 0.15s ease-in-out; /* Transition douce */
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); /* Ombre légère */
}

.color-picker-toggle:hover {
  background-color: #f3f4f6; /* Fond légèrement plus foncé au survol */
  border-color: #9ca3af; /* Bordure légèrement plus foncée au survol */
}

/* Optionnel: Style au focus pour l'accessibilité */
.color-picker-toggle:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  /* Adaptez la couleur du focus si besoin (ici, basée sur le rouge AGCO) */
  box-shadow: 0 0 0 2px rgba(183, 0, 24, 0.5);
}

/* Optionnel: Style lors du clic */
.color-picker-toggle:active {
  background-color: #e5e7eb; /* Fond encore plus foncé lors du clic */
}

.hidden-color-input {
  /* Rend l'input invisible mais potentiellement plus "cliquable" par programme */
  position: absolute;
  opacity: 0;
  width: 1px; /* Donner une taille minimale */
  height: 1px; /* Donner une taille minimale */
  pointer-events: none; /* Garder pour ne pas intercepter les clics souris */
}

.preview-mode .color-picker-toggle {
  display: none !important;
}

.preview-mode .support-header {
  border: none !important;
}

/* Style spécifique pour les images des 3 produits du bas dans le layout à 6 produits */
.products-container[class*="grid-6"]
  .flex-row:last-child
  .product-zone
  :deep(.image-container) {
  /* min-height: 35px; */
  max-height: 150px;
  padding: 0.15rem;
}

.products-container[class*="grid-6"]
  .flex-row:last-child
  .product-zone
  :deep(.product-content) {
  padding: 0.15rem;
  gap: 0.15rem;
}

.products-container[class*="grid-6"]
  .flex-row:last-child
  .product-zone
  :deep(.product-main) {
  gap: 0.15rem;
}

.products-container[class*="grid-6"]
  .flex-row:last-child
  .product-zone
  :deep(.product-title),
.products-container[class*="grid-6"]
  .flex-row:last-child
  .product-zone
  :deep(.product-description) {
  font-size: 0.8em;
  margin: 0.15rem 0;
}

.products-container[class*="grid-6"]
  .flex-row:last-child
  .product-zone
  :deep(.product-price) {
  font-size: 0.8em;
  margin-top: 0.15rem;
}

.support-footer {
  transition: height 0.3s ease;
  height: 115px;
}

[contenteditable="true"]:focus {
  outline: 2px solid #b70018;
  border-radius: 4px;
  padding: 0 2px;
}

[contenteditable="true"]:empty:before {
  content: attr(placeholder);
  color: #9ca3af;
  cursor: text;
}

.toggle-validity-button {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  padding: 0;
}

.toggle-validity-button:hover {
  border-color: #b70018;
  background: #f8f8f8;
}

.toggle-validity-button .icon {
  font-size: 1rem;
}

.validity-text-container {
  width: 500px;
  height: 24px;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

/* Style spécifique pour les 2 produits de droite dans le layout à 3 produits */
.products-container[class*="grid-3"] .flex-row > .flex-column .product-zone {
  height: 30%;
  min-height: 150px;
  margin-bottom: 0.5rem;
}

.products-container[class*="grid-3"]
  .flex-row
  > .flex-column
  :deep(.product-image-container) {
  height: 80px;
  width: 80px;
  margin: 0 auto;
}

.products-container[class*="grid-3"]
  .flex-row
  > .flex-column
  :deep(.product-image) {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.products-container[class*="grid-3"]
  .flex-row
  > .flex-column
  :deep(.product-content) {
  padding: 0.25rem;
}

.products-container[class*="grid-3"]
  .flex-row
  > .flex-column
  :deep(.product-title),
.products-container[class*="grid-3"]
  .flex-row
  > .flex-column
  :deep(.product-description) {
  font-size: 0.75em;
  margin: 0.25rem 0;
}

.products-container[class*="grid-3"]
  .flex-row
  > .flex-column
  :deep(.product-price) {
  font-size: 0.85em;
  margin-top: 0.25rem;
}

.support-canvas__save-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.support-canvas__save-btn:hover {
  background-color: var(--primary-color-dark);
}

.support-canvas__save-btn:disabled {
  background-color: var(--gray-400);
  cursor: not-allowed;
}

.support-canvas__save-btn i {
  font-size: 1rem;
}

.legal-text {
  font-size: 0.75rem;
  line-height: 1.2;
}

.legal-text-small {
  font-size: 0.65rem;
  line-height: 1.1;
}

/* Styles pour l'encart de repiquage */
.support-footer .repiquage-container {
  height: 115px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.support-footer .repiquage-container img {
  transition: transform 0.2s ease;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform-origin: center center;
}

/* Ajuster la taille du texte légal quand il y a un repiquage */
.support-footer .legal-text {
  transition: font-size 0.3s ease;
}

.support-footer .legal-text-small {
  font-size: 0.65rem;
  line-height: 1.1;
}

/* Style pour le conteneur de zoom dans la modale */
.zoom-preview-container {
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #f9fafb;
  border-radius: 0.5rem;
  margin: 1rem 0;
}

.zoom-preview-container img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease;
}

/* Ajustements pour le mode aperçu */
.preview-mode .support-footer .repiquage-container {
  border: none;
}

.preview-mode .support-footer .repiquage-container img {
  max-height: 100px;
  max-width: 280px;
}

/* Style pour le slider de zoom */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  outline: none;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: #b70018;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
}

input[type="range"]::-webkit-slider-thumb:hover {
  background: #8c0014;
}

input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #b70018;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  transition: background 0.2s;
}

input[type="range"]::-moz-range-thumb:hover {
  background: #8c0014;
}

.support-footer .flex-row {
  align-items: stretch;
}
.support-footer .flex-col {
  justify-content: center;
  height: 100%;
}

.pdf-capture-mode,
.pdf-capture-mode * {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
  /* background: #fff !important; */
  caret-color: transparent !important;
}
.pdf-capture-mode [contenteditable] {
  outline: none !important;
  border: none !important;
  background: transparent !important;
  caret-color: transparent !important;
  user-select: none !important;
  pointer-events: none !important;
}

.pdf-capture-mode .product-zone img,
.pdf-capture-mode .product-block img {
  width: 160px !important;
  height: 160px !important;
  object-fit: contain !important;
  display: block !important;
  margin: 0 auto !important;
}
.pdf-capture-mode .product-zone {
  min-height: 220px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-direction: column !important;
}

.pdf-capture-mode
  .products-container.grid-2
  .product-zone:nth-child(2)
  .product-block
  .product-content,
.products-container.grid-2
  .product-zone:nth-child(2)
  .product-block
  .product-content {
  flex-direction: row-reverse !important;
}

.rowTwo .product-content {
  flex-direction: row-reverse !important;
}

/* Styles pour l'overlay de chargement */
.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.pdf-loading-content {
  text-align: center;
}

.loader-svg {
  width: 320px;
  height: 170px;
  margin-bottom: 1.5rem;
}

.gear-main {
  transform-origin: 70px 90px;
  animation: gear-rotate 2s linear infinite;
}

.gear-small {
  transform-box: fill-box;
  transform-origin: 50% 50%;
}

.gear1 {
  animation: gear-rotate 1.2s linear infinite reverse;
}

.gear2 {
  animation: gear-rotate 1.6s linear infinite;
}

.gear3 {
  animation: gear-rotate 2.1s linear infinite reverse;
}

@keyframes gear-rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  color: #2d3a3a;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.pdf-capture-mode .pdf-loading-overlay {
  opacity: 0;
  pointer-events: none;
}

.loader-standard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.spinner {
  width: 56px;
  height: 56px;
  border: 6px solid #e5e7eb;
  border-top: 6px solid #b70018;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Styles pour la modal de sélection des pages */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.modal-close:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
}

.modal-description {
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.page-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.page-option {
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.page-option:hover {
  border-color: #b70018;
  background-color: #fef2f2;
}

.page-radio {
  margin-right: 0.75rem;
  width: 1.25rem;
  height: 1.25rem;
  accent-color: #b70018;
}

.page-option-text {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #374151;
}

.page-option-text i {
  margin-right: 0.5rem;
  color: #b70018;
  font-size: 1.125rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.btn-secondary {
  padding: 0.75rem 1.5rem;
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background-color: #e5e7eb;
  border-color: #9ca3af;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  background-color: #b70018;
  color: white;
  border: 1px solid #b70018;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background-color: #8c0014;
  border-color: #8c0014;
}

/* Adaptation du footer en mode verso */
.verso-mode .support-footer .flex-row {
  gap: 0; /* Pas d'espace entre les colonnes en verso */
}

.verso-mode .support-footer .flex-col.w-full {
  flex: 1;
  text-align: center; /* Centrer le contenu */
}

.verso-mode .support-footer .legal-text {
  font-size: 0.8rem; /* Texte légèrement plus grand en verso */
  line-height: 1.3;
}

.verso-mode .support-footer .toggle-validity-button {
  left: 50%;
  transform: translateX(-50%);
}

/* Styles pour les boutons PDF et HTML */
.support-canvas__pdf-btn,
.support-canvas__html-btn {
  padding: 0.5rem 1rem;
  background-color: #b70018;
  color: white;
  border: 1px solid #b70018;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.support-canvas__pdf-btn:hover,
.support-canvas__html-btn:hover {
  background-color: #8c0014;
  border-color: #8c0014;
}

.support-canvas__pdf-btn:disabled,
.support-canvas__html-btn:disabled {
  background-color: #9ca3af;
  border-color: #9ca3af;
  cursor: not-allowed;
}

.support-canvas__html-btn {
  background-color: #059669;
  border-color: #059669;
}

.support-canvas__html-btn:hover {
  background-color: #047857;
  border-color: #047857;
}
</style>
